import React from "react";
import { BsDiscord, BsTelegram, BsTwitter, BsGlobe, BsLink45Deg, BsCalendar, BsChatDots } from "react-icons/bs";
import { SiNotion } from "react-icons/si";

const linkFields = [
  {
    name: "discord",
    label: "Discord",
    icon: BsDiscord,
    placeholder: "https://discord.gg/...",
    color: "text-indigo-500",
  },
  {
    name: "telegram",
    label: "Telegram",
    icon: BsTelegram,
    placeholder: "https://t.me/...",
    color: "text-blue-500",
  },
  {
    name: "twitter",
    label: "X (Twitter)",
    icon: BsTwitter,
    placeholder: "https://twitter.com/... or @handle",
    color: "text-sky-500",
  },
  {
    name: "website",
    label: "Website",
    icon: BsGlobe,
    placeholder: "https://your-community.com",
    color: "text-green-500",
  },
  {
    name: "forum",
    label: "Forum / Subreddit",
    icon: BsLink45Deg,
    placeholder: "https://reddit.com/r/... or forum URL",
    color: "text-orange-500",
  },
  {
    name: "notion",
    label: "Notion / Docs",
    icon: SiNotion,
    placeholder: "https://notion.so/...",
    color: "text-gray-700",
  },
  {
    name: "calendar",
    label: "Event Calendar",
    icon: BsCalendar,
    placeholder: "https://calendar.google.com/...",
    color: "text-red-500",
  },
];

const Step2CommunityLinks = ({ formik }) => {
  const handleLinkChange = (name, value) => {
    formik.setFieldValue(`links.${name}`, value);
  };

  // Check if at least one link is provided
  const hasAtLeastOneLink = Object.values(formik.values.links || {}).some(
    (link) => link && link.trim() !== ""
  );

  return (
    <div className="space-y-5 md:space-y-6">
      <div className="text-center mb-6 md:mb-8">
        <div className="inline-flex items-center justify-center w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-gradient-to-br from-blue-100 to-indigo-100 mb-3">
          <BsChatDots className="text-2xl md:text-3xl text-blue-600" />
        </div>
        <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-2">
          Community Links
        </h2>
        <p className="text-sm md:text-base text-gray-600">
          Add links where members can connect with your community
        </p>
        <p className="text-xs md:text-sm text-purple-600 mt-2 font-medium">
          ✨ At least one communication link is required
        </p>
      </div>

      {/* Validation Notice */}
      {formik.touched.links && !hasAtLeastOneLink && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">
          Please provide at least one community link (Discord, Telegram, etc.)
        </div>
      )}

      {/* Link Fields */}
      <div className="space-y-4">
        {linkFields.map((field) => {
          const Icon = field.icon;
          const value = formik.values.links?.[field.name] || "";
          const isValid = value && value.trim() !== "";

          return (
            <div key={field.name} className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <Icon className={`${field.color}`} />
                {field.label}
                {field.name === "discord" || field.name === "telegram" ? (
                  <span className="text-purple-500 text-xs">(Recommended)</span>
                ) : null}
              </label>
              <div className="relative">
                <input
                  type="url"
                  name={`links.${field.name}`}
                  value={value}
                  onChange={(e) => handleLinkChange(field.name, e.target.value)}
                  placeholder={field.placeholder}
                  className={`w-full pl-12 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent transition-all ${
                    isValid
                      ? "border-green-300 bg-green-50"
                      : "border-gray-200"
                  }`}
                />
                <div className="absolute left-4 top-1/2 -translate-y-1/2">
                  <Icon className={`text-xl ${isValid ? field.color : "text-gray-400"}`} />
                </div>
                {isValid && (
                  <div className="absolute right-4 top-1/2 -translate-y-1/2">
                    <svg
                      className="w-5 h-5 text-green-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Tips Section */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-2xl p-6 mt-8">
        <h4 className="font-semibold text-gray-800 mb-3">💡 Tips for Community Links</h4>
        <ul className="space-y-2 text-sm text-gray-600">
          <li className="flex items-start gap-2">
            <span className="text-purple-500">•</span>
            <span>Discord and Telegram are the most popular platforms for DeSci communities</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-purple-500">•</span>
            <span>Consider creating a dedicated channel for NFT holders</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-purple-500">•</span>
            <span>Use token-gating tools like Guild.xyz or Collab.Land for access control</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-purple-500">•</span>
            <span>A Notion doc with community rules and FAQs helps new members</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default Step2CommunityLinks;
